import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { WAVE_VOTING_LABELS, WAVE_VOTE_STATS_LABELS } from "@/helpers/waves/waves.constants";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
interface WaveDropVoteSummaryProps {
  readonly drop: ExtendedDrop;
  readonly isWinner: boolean;
  readonly isVotingEnded: boolean;
  readonly canShowVote: boolean;
  readonly onVoteClick: () => void;
  readonly winningThreshold?: number | null | undefined;
}
const PRIMARY_BUTTON_CLASSES = "tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-flex tw-items-center tw-rounded-lg tw-bg-iron-200 tw-text-iron-950 tw-border-0 tw-ring-1 tw-ring-inset tw-ring-white hover:tw-bg-iron-300 hover:tw-ring-iron-300 tw-transition tw-duration-300 tw-ease-out tw-cursor-pointer";
export const WaveDropVoteSummary = t0 => {
  const $ = _c(27);
  const {
    drop,
    isWinner,
    isVotingEnded,
    canShowVote,
    onVoteClick,
    winningThreshold
  } = t0;
  const hasUserVoted = drop.context_profile_context?.rating !== undefined && drop.context_profile_context?.rating !== 0;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const isUserVoteNegative = userVote < 0;
  const shouldShowUserVote = (isVotingEnded || isWinner) && hasUserVoted;
  const isApproveVoteSummary = typeof winningThreshold === "number" && Number.isFinite(winningThreshold) && winningThreshold > 0;
  const progressVote = isApproveVoteSummary ? drop.realtime_rating : drop.rating_prediction;
  const progressTooltipLabel = isApproveVoteSummary ? "Votes given now" : "Projected vote count at decision time";
  let t1;
  if ($[0] !== drop.rating) {
    t1 = formatNumberWithCommas(drop.rating);
    $[0] = drop.rating;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="tw-text-sm tw-font-semibold tw-tabular-nums tw-text-white sm:tw-text-base">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== drop.id || $[5] !== drop.rating || $[6] !== progressTooltipLabel || $[7] !== progressVote) {
    t3 = drop.rating !== progressVote && <><FontAwesomeIcon icon={faArrowRight} className="tw-size-2.5 tw-flex-shrink-0 tw-text-iron-600" /><span className={`tw-cursor-help tw-text-sm tw-font-semibold tw-tabular-nums sm:tw-text-base ${drop.rating < progressVote ? "tw-text-emerald-400" : "tw-text-rose-400"}`} data-tooltip-id={`drop-vote-progress-${drop.id}`}>{formatNumberWithCommas(progressVote)}</span><Tooltip id={`drop-vote-progress-${drop.id}`} place="top" offset={8} opacity={1} style={TOOLTIP_STYLES}>{progressTooltipLabel}: {formatNumberWithCommas(progressVote)}</Tooltip></>;
    $[4] = drop.id;
    $[5] = drop.rating;
    $[6] = progressTooltipLabel;
    $[7] = progressVote;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="tw-whitespace-nowrap tw-text-sm tw-font-normal tw-text-iron-500 sm:tw-text-base">{t4} Total</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t5) {
    t6 = <div className="tw-flex tw-cursor-default tw-flex-wrap tw-items-baseline tw-gap-1.5 tw-px-4">{t2}{t3}{t5}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== drop.wave.voting_credit_type || $[16] !== isUserVoteNegative || $[17] !== shouldShowUserVote || $[18] !== userVote) {
    t7 = shouldShowUserVote && <div className="tw-flex tw-items-baseline tw-gap-1 tw-border-y-0 tw-border-l tw-border-r-0 tw-border-solid tw-border-white/5 tw-px-4"><span className="tw-text-sm tw-font-normal tw-text-iron-500 sm:tw-text-base">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:</span><span className={`tw-text-sm tw-font-semibold sm:tw-text-base ${isUserVoteNegative ? "tw-text-rose-500" : "tw-text-emerald-500"}`}>{isUserVoteNegative && "-"}{formatNumberWithCommas(Math.abs(userVote))}</span><span className="tw-text-sm tw-font-normal tw-text-iron-500 sm:tw-text-base">{WAVE_VOTING_LABELS[drop.wave.voting_credit_type]}</span></div>;
    $[15] = drop.wave.voting_credit_type;
    $[16] = isUserVoteNegative;
    $[17] = shouldShowUserVote;
    $[18] = userVote;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== canShowVote || $[21] !== onVoteClick) {
    t8 = canShowVote && <button type="button" onClick={onVoteClick} className={PRIMARY_BUTTON_CLASSES}>Vote</button>;
    $[20] = canShowVote;
    $[21] = onVoteClick;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = <div className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-p-1.5 tw-shadow-2xl tw-transition-transform hover:tw-scale-[1.01] sm:tw-gap-3">{t6}{t7}{t8}</div>;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};